import type {
  PieceState,
  RecoveryBasis,
  RecoveryOutcome,
  RecoveryStoredBasis,
  RecoveryStoredOutcome,
  RecoverySubjectStatus,
} from '@/lib/api/schemas/product-recovery.schema'
import type { CandidateStrength } from '@/lib/recovery/ranking'
import { unavailableReasonText } from '@/lib/warehouse/labels'

/** Shown wherever a fact is absent and nothing stored says why. */
export const ABSENT_FACT = '—'

export const pieceStateLabels: Record<PieceState, string> = {
  in_stock: 'В наличии',
  in_transit: 'В доставке',
  sold: 'Продано',
}

/**
 * What the strength of a candidate's evidence means, in the operator's words.
 *
 * A sole sold piece is the strongest thing this screen can say and it is still
 * not an answer: the sentence names what was recorded, never what follows.
 */
export const candidateStrengthLabels: Record<CandidateStrength, string> = {
  sole_sold_piece: 'Единственное проданное изделие артикула',
  one_of_several_sold: 'Одно из нескольких проданных изделий артикула',
  not_sold: 'Продано не оно',
  no_sold_piece_in_article: 'Ни одно изделие артикула не отмечено проданным',
  state_not_recorded: 'Состояние изделия не записано',
}

export const outcomeLabels: Record<RecoveryOutcome, string> = {
  matched: 'Это изделие',
  not_any_of_these: 'Ни одно из них',
  cannot_determine: 'Не могу определить',
}

/**
 * The basis, spelled so the two can never read as the same thing.
 *
 * A resemblance never becomes the owner of a sale. The sentence an operator
 * chooses is the sentence every later reader sees.
 */
export const basisLabels: Record<RecoveryBasis, string> = {
  exact_source_identity: 'Точный факт источника',
  operator_recognition: 'Узнавание оператором',
}

export const subjectStatusLabels: Record<RecoverySubjectStatus, string> = {
  open: 'Ждёт решения',
  held: 'Ожидает решения владельца',
  cannot_determine: 'Определить не удалось',
  closed: 'Решение принято',
}

/**
 * The stored vocabularies, which are wider than the ones an operator can choose.
 * A machine's decision reads as a machine's decision rather than being dressed
 * as somebody's.
 */
export const storedOutcomeLabels: Record<RecoveryStoredOutcome, string> = {
  matched: 'Сопоставлено с изделием',
  none_of_these: 'Ни одно из предложенных',
  cannot_determine: 'Определить не удалось',
  deleted: 'Пустая запись удалена',
  distributed: 'Распределено по изделиям',
}

export const storedBasisLabels: Record<RecoveryStoredBasis, string> = {
  exact_source_identity: 'Точный факт источника',
  exact_article_singleton: 'Единственное изделие артикула, определено автоматически',
  operator_recognition: 'Узнавание оператором',
  evidence_review: 'Разбор доказательств без выбора изделия',
  unattached_no_candidate: 'Ни связей, ни кандидата',
  article_group_choice: 'Системный выбор среди одинаковых изделий',
}

/**
 * The relations a decision can move. An unknown key is printed as itself: a
 * relation this build has not seen is a real thing that moved, and naming it by
 * its own key is honest where inventing a Russian noun for it would not be.
 */
const relationLabels: Record<string, string> = {
  'forge.product_sales_link': 'Продажи',
  'portal.sales_order_item': 'Позиции заказов',
  'forge.product_order_relation': 'Связи с заказами',
  'forge.marketplace_listing': 'Объявления',
  'forge.product_media': 'Фотографии',
  'forge.inventory_movement': 'Движения склада',
  'forge.stock_fact': 'Остатки',
  'forge.price_fact': 'Цены',
  'forge.product_content': 'Описание исходной записи',
  'forge.product': 'Объединения, ожидающие владельца',
}

export function relationLabel(key: string): string {
  return relationLabels[key] ?? key
}

/**
 * Why a legacy row is in this queue at all. Read leniently, exactly as the
 * sibling surface reads a match reason: a code this build has not seen is shown
 * as its own token rather than given an invented meaning.
 */
const subjectReasonLabels: Record<string, string> = {
  multiple_candidates: 'Артикул совпал сразу с несколькими изделиями.',
  single_candidate: 'Артикул называет одно изделие.',
  no_candidate: 'Ни одно изделие не несёт этот артикул.',
  previously_merged: 'Запись уже была объединена с другой. Решение по ней принимает владелец.',
  relation_conflict: 'Автоматический перенос связей был отклонён проверкой.',
  distribution_cancelled_order: 'Продажа по отменённому заказу оставлена на исходной записи.',
  distribution_owner_held_merge: 'Исходную запись удерживает объединение, ожидающее владельца.',
  distribution_unresolved_listing: 'Объявление не подтверждено точным предложением 1С.',
  distribution_unresolved_sales_link: 'Продажа не подтверждена точным предложением 1С.',
  distribution_unresolved_fact: 'Факт цены или остатка не подтверждён одним предложением.',
  distribution_size_mismatch: 'Размер связи не совпадает с размером изделия.',
  distribution_media_remains: 'На исходной записи остались фотографии.',
  distribution_order_relation_remains: 'На исходной записи осталась связь с заказом.',
  distribution_other_relation_remains: 'На исходной записи осталась непроверенная связь.',
}

export function subjectReasonText(code: string): string {
  return subjectReasonLabels[code] ?? code
}

/**
 * The sentence behind a dash, for the two facts that carry a stored reason.
 *
 * `forge.product` enforces `num_nonnulls(value, reason) = 1` on state and on
 * weight, so those two absences arrive explained. The codes below are the
 * column defaults; anything else falls through to the Warehouse vocabulary, so
 * one absence keeps one sentence across both screens rather than two spellings.
 */
const storedReasonLabels: Record<string, string> = {
  'state.not_recorded': 'Состояние изделия не записано',
  'weight.not_recorded': 'Авторитетный вес изделия не зафиксирован',
}

export function storedReasonText(code: string): string {
  return storedReasonLabels[code] ?? unavailableReasonText(code)
}
